import { env } from '../utils/env.js';

class LocationDataLoader {
    constructor() {
        this.store = {};
        // Set to true during development to always fetch fresh data from API and overwrite localStorage
        // Set to false in production to leverage cached localStorage data
        this.overwriteLocalStorage = false;
    }

    async loadLocationDataFromAPI() {
        const API_BASE = env.VITE_API_BASE;

        try {
            // Check if data already exists in localStorage (only if not forcing overwrite)
            if (!this.overwriteLocalStorage) {
                const existingData = this.loadFromLocalStorage();
                if (existingData) {
                    this.store = existingData;
                    // Quick progress animation for localStorage load
                    const { animateQuickProgress } = await import('../OverworldAnimation.js');
                    await animateQuickProgress(
                        'LOCATION_DATA',
                        `Loaded ${Object.keys(existingData).length} locations from cache`
                    );
                    return;
                }
            }

            // Data doesn't exist or overwriteLocalStorage is true, load from API
            const { updateLoadingProgress } = await import('../OverworldAnimation.js');
            updateLoadingProgress('LOCATION_DATA', 'Loading location data from API...');

            // Fetch all location data in a single request
            const response = await fetch(`${API_BASE}/encounters`);

            if (!response.ok) {
                throw new Error(`Failed to fetch location data: ${response.status} ${response.statusText}`);
            }

            const apiLocationData = await response.json();

            if (typeof apiLocationData !== 'object' || apiLocationData === null) {
                throw new Error('API response is not an object');
            }

            const locationDatabase = {};
            let loadedCount = 0;

            // Transform each location from API response
            Object.keys(apiLocationData).forEach((locationIdentifier) => {
                try {
                    const apiData = apiLocationData[locationIdentifier];

                    // Transform API response to match expected format
                    const locationData = {
                        identifier: apiData.locationId,
                        name: apiData.name,
                        encounterRate: apiData.encounterRate || 0,
                        encounterRateRockSmash: apiData.encounterRateRockSmash ?? 1.0,
                        encounterRateCut: apiData.encounterRateCut ?? 1.0,
                        canBike: apiData.canBike !== undefined ? apiData.canBike : true,
                        needsFlash: apiData.needsFlash !== undefined ? apiData.needsFlash : false,
                        fixedTimeOfDay: apiData.fixedTimeOfDay ?? null, // Preserve null values, don't default to 'day'
                        encounters: apiData.encounters || null,
                        // TODO(pkmng-143): remove the null fallback once the API exposes this field.
                        townMapPosition: apiData.townMapPosition ?? null,
                    };

                    locationDatabase[apiData.locationId] = locationData;
                    loadedCount++;
                } catch (error) {
                    console.error(`Error processing location ${locationIdentifier}: ${error.message}`);
                }
            });

            // Save the complete database to localStorage and memory
            this.store = locationDatabase;
            this.saveToLocalStorage(locationDatabase);

            console.log(`Location data loading from API completed! Loaded ${loadedCount} locations.`);
        } catch (error) {
            console.error(`Error loading location data from API: ${error.message}`);
        }
    }

    // Save the complete location database to localStorage
    saveToLocalStorage(locationDatabase) {
        try {
            localStorage.setItem('location_data', JSON.stringify(locationDatabase));
            console.log('Location database saved to localStorage');
        } catch (storageError) {
            console.error(`Failed to save location database to localStorage: ${storageError.message}`);
        }
    }

    // Load the complete location database from localStorage
    loadFromLocalStorage() {
        try {
            const storedData = localStorage.getItem('location_data');
            if (storedData) {
                return JSON.parse(storedData);
            }
        } catch (error) {
            console.error(`Error loading location data from localStorage: ${error.message}`);
        }
        return null;
    }

    // Helper method to retrieve location data by identifier
    getLocationData(locationIdentifier) {
        return this.store[locationIdentifier] || null;
    }

    // Helper method to get all location identifiers
    getAllLocationIdentifiers() {
        return Object.keys(this.store);
    }

    // Helper method to get all location data
    getAllLocations() {
        return this.store;
    }

    // Helper method to clear all location data
    clearAllLocationData() {
        try {
            localStorage.removeItem('location_data');
            console.log('Cleared location data from localStorage');
        } catch (error) {
            console.error(`Error clearing localStorage: ${error.message}`);
        }

        // Clear memory store
        this.store = {};
    }
}

export const locationDataLoader = new LocationDataLoader();
